"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "@/hooks/useToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { validateFields } from "@/lib/utils";
import type { FormProps, FormState, Section, Category, MetadataItem } from "./types";

const INITIAL_STATE: FormState = {
  componentFields: {},
  metadata: [],
  metadata_1: [],
  categories: [],
  componentFieldErrors: {},
  componentFieldMessages: {},
};

export function useForm8({
  selectedSection,
  pageId,
  pageComponentId,
  variantDetails,
}: Pick<FormProps, "selectedSection" | "pageId" | "pageComponentId" | "variantDetails">) {
  const [formState, setFormState] = useState<FormState>(INITIAL_STATE);
  const prevSectionRef = useRef<{ id?: string; componentId?: string }>({});
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user?.user);

  const reduxState = useAppSelector((state) =>
    variantDetails?.selector ? variantDetails.selector(state.page as any) : null
  ) as Record<string, any> | null;

  const componentFieldsConfig = variantDetails?.componentFields || [];
  // Persist keys are configurable so this form can back non-fee variants too.
  const categoriesKey = variantDetails?.categoriesKey || "categories";
  const itemsKey = variantDetails?.itemsKey || "metadata";

  // ── Load data from section or Redux ────────────────────────────────────
  useEffect(() => {
    if (!variantDetails) return;

    const hasChanged =
      prevSectionRef.current?.id !== selectedSection.id ||
      prevSectionRef.current?.componentId !== selectedSection?.componentId;

    if (!hasChanged && Object.keys(formState.componentFields).length > 0) return;

    prevSectionRef.current = {
      id: selectedSection.id,
      componentId: selectedSection?.componentId,
    };

    const data =
      selectedSection?.data && Object.keys(selectedSection.data).length > 0
        ? selectedSection.data
        : reduxState || {};

    // Extract component fields (everything except metadata arrays and the repeater array)
    const { metadata, metadata_1, ...restData } = data;
    const categories = restData[categoriesKey];
    const componentData = { ...restData };
    delete componentData[categoriesKey];

    setFormState({
      componentFields: componentData,
      metadata: Array.isArray(metadata) ? metadata : [],
      metadata_1: Array.isArray(metadata_1) ? metadata_1 : [],
      categories: Array.isArray(categories) ? categories : [],
      componentFieldErrors: {},
      componentFieldMessages: {},
    });

    if (selectedSection?.data && variantDetails.setAction) {
      dispatch(variantDetails.setAction(data));
    }
  }, [
    selectedSection.id,
    selectedSection?.componentId,
    selectedSection?.data,
    reduxState,
    variantDetails,
    dispatch,
    formState.componentFields,
  ]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleComponentFieldsUpdate = useCallback(
    (updatedData: Record<string, any>) => {
      setFormState((prev) => ({ ...prev, componentFields: updatedData }));
    },
    []
  );

  const handleMetadataUpdate = useCallback((updatedData: MetadataItem[]) => {
    setFormState((prev) => ({ ...prev, metadata: updatedData }));
  }, []);

  const handleMetadata1Update = useCallback((updatedData: MetadataItem[]) => {
    setFormState((prev) => ({ ...prev, metadata_1: updatedData }));
  }, []);

  // ── Category Operations ────────────────────────────────────────────────

  const handleAddCategory = useCallback(() => {
    const newCategory: Category = {
      id: `cat_${Date.now()}`,
      title: `${variantDetails?.categorySingularLabel || "Category"} ${
        formState.categories.length + 1
      }`,
      active_bg_color: "#02a7b6",
      active_text_color: "#ffffff",
      order_index: formState.categories.length,
      [itemsKey]: [],
    };
    setFormState((prev) => ({
      ...prev,
      categories: [...prev.categories, newCategory],
    }));
  }, [formState.categories.length, itemsKey, variantDetails]);

  const handleDeleteCategory = useCallback((index: number) => {
    setFormState((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index),
    }));
  }, []);

  const handleUpdateCategory = useCallback(
    (index: number, field: string, value: any) => {
      setFormState((prev) => {
        const updated = [...prev.categories];
        updated[index] = { ...updated[index], [field]: value };
        return { ...prev, categories: updated };
      });
    },
    []
  );

  // Merge a partial object (e.g. from a per-category FieldsFormatter) into a category.
  const handleUpdateCategoryFields = useCallback(
    (index: number, partial: Record<string, any>) => {
      setFormState((prev) => {
        const updated = [...prev.categories];
        updated[index] = { ...updated[index], ...partial };
        return { ...prev, categories: updated };
      });
    },
    []
  );

  const handleUpdateCategoryMetadata = useCallback(
    (categoryIndex: number, updatedData: MetadataItem[]) => {
      setFormState((prev) => {
        const updated = [...prev.categories];
        updated[categoryIndex] = {
          ...updated[categoryIndex],
          [itemsKey]: updatedData,
        };
        return { ...prev, categories: updated };
      });
    },
    [itemsKey]
  );

  // ── Save ───────────────────────────────────────────────────────────────

  const handleSave = useCallback(
    async (onSave: (updatedSection: Section) => void) => {
      if (!variantDetails) {
        toast({
          title: "Error",
          description: "Widget configuration not found",
          variant: "destructive",
        });
        return;
      }

      // Validate component fields
      const { errors, messages, hasErrors } = validateFields(
        componentFieldsConfig,
        formState.componentFields
      );
      if (hasErrors) {
        setFormState((prev) => ({
          ...prev,
          componentFieldErrors: errors,
          componentFieldMessages: messages,
        }));
        toast({
          title: "Validation Error",
          description: "Please fix the errors before saving",
          variant: "destructive",
        });
        return;
      }

      try {
        const componentId =
          reduxState?.id || formState.componentFields.id;
        const isUpdate =
          componentId && !String(componentId).startsWith("temp_");

        const payload = {
          ...(isUpdate && { id: componentId }),
          ...formState.componentFields,
          metadata: formState.metadata,
          metadata_1: formState.metadata_1,
          [categoriesKey]: formState.categories,
          variant: variantDetails.id,
          page_id: pageId ? Number(pageId) : undefined,
          component_id: pageComponentId ? Number(pageComponentId) : undefined,
          ...(isUpdate
            ? { updated_by: currentUser?.id }
            : { created_by: currentUser?.id }),
        };

        let responseId: string;

        if (isUpdate && variantDetails.updateMethod) {
          await variantDetails.updateMethod(Number(componentId), payload);
          responseId = String(componentId);
        } else if (variantDetails.createMethod) {
          const result = await variantDetails.createMethod(payload);
          responseId = String(result?.data?.id || result?.id);
        } else {
          throw new Error("No save method configured");
        }

        if (!responseId) throw new Error("Failed to get ID from response");

        // Update Redux
        const savedData = {
          ...formState.componentFields,
          id: responseId,
          metadata: formState.metadata,
          metadata_1: formState.metadata_1,
          [categoriesKey]: formState.categories,
        };

        if (variantDetails.setAction) {
          dispatch(variantDetails.setAction(savedData));
        }

        setFormState((prev) => ({
          ...prev,
          componentFields: { ...prev.componentFields, id: responseId },
        }));

        toast({
          title: "Success",
          description: "Widget settings saved successfully",
        });

        onSave({
          ...selectedSection,
          componentId: responseId,
          data: savedData,
        });
      } catch (error: any) {
        console.error("Failed to save:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to save widget settings",
          variant: "destructive",
        });
      }
    },
    [
      variantDetails,
      componentFieldsConfig,
      formState,
      reduxState,
      pageId,
      pageComponentId,
      currentUser?.id,
      dispatch,
      selectedSection,
    ]
  );

  return {
    formState,
    componentFieldsConfig,
    handleComponentFieldsUpdate,
    handleMetadataUpdate,
    handleMetadata1Update,
    handleAddCategory,
    handleDeleteCategory,
    handleUpdateCategory,
    handleUpdateCategoryFields,
    handleUpdateCategoryMetadata,
    handleSave,
    variantDetails,
  };
}
